import type { Locale, SupportContent } from "./types";

export const supportByLocale: Record<Locale, SupportContent> = {
  en: {
    title: "Support",
    introTitle: "How can we help?",
    introBody:
      "Moodshot is a small app made by one person. If something is broken, confusing, or missing, an email is the fastest way to get it looked at.",
    contactHeading: "Contact us",
    contactDescription: "We read every message and usually reply within a few days. Please write in English or Chinese.",
    email: "axqora@gmail.com",
    reportHeading: "When reporting an issue, please include",
    reportItems: [
      "Device model (for example, iPhone 15 or iPad Air)",
      "iOS version",
      "Moodshot version, shown in Settings → About",
      "What you did step by step, and what happened instead",
      "A screenshot, if you have one (it helps a lot)"
    ],
    faqHeading: "Frequently asked questions",
    faqs: [
      {
        question: "It only plays 30 seconds of each song",
        answer:
          "That is the preview mode, used when the Apple ID on the device has no active Apple Music subscription. With a subscription the app plays full tracks. If you do have one, check that Moodshot is allowed under Settings → Privacy & Security → Media & Apple Music."
      },
      {
        question: "It says it can't find a playlist",
        answer:
          "Recognition works offline, but fetching the playlist needs a network. Check your connection: the app automatically re-runs the search once you are back online."
      },
      {
        question: "The mood it picked feels wrong",
        answer:
          "Tap one of the names under OTHER PRESSINGS to switch to the runner-up moods. The line under the mood name shows what the app actually recognized, which usually explains the choice. A plain wall or a very dark frame gives it little to go on, and it falls back to the overall colour and the time of day."
      },
      {
        question: "Where are my saved moments kept?",
        answer:
          "Inside the app on your device. They are never uploaded to our servers, so they cannot be recovered by us. iCloud sync is on by default, and while it is on they are also copied to your own private iCloud database \u2014 a space only you can access, so we still cannot see or recover them from there either. You can turn sync off and remove that copy anytime in Settings. Deleting the app deletes them from this device; they are included in your own device backups if you have those enabled."
      },
      {
        question: "Does it upload my photos?",
        answer: "Not to our servers. Photos are analysed on the device. iCloud sync is on by default, and while it is on, saved moments are copied to your own private iCloud database, which we cannot see either \u2014 you can turn sync off and remove that copy anytime in Settings."
      }
    ]
  },
  zh: {
    title: "用户支持",
    introTitle: "需要什么帮助？",
    introBody: "Moodshot 是一个人做的小 App。哪里坏了、看不明白或者少了什么，发邮件是最快能被处理的方式。",
    contactHeading: "联系我们",
    contactDescription: "每封邮件都会看，通常几天内回复。中文或英文均可。",
    email: "axqora@gmail.com",
    reportHeading: "反馈问题时请附上",
    reportItems: [
      "设备型号（例如 iPhone 15 或 iPad Air）",
      "iOS 版本",
      "Moodshot 版本，在设置 → 关于里",
      "问题发生前的具体操作步骤，以及实际发生了什么",
      "截图（附上截图有助于我们更快排查）"
    ],
    faqHeading: "常见问题",
    faqs: [
      {
        question: "每首歌只播 30 秒",
        answer:
          "这是试听模式。当当前设备所登 Apple ID 未开通有效 Apple Music 订阅时，系统会播放 Apple 官方提供的 30 秒试听。拥有有效订阅后即可收听完整曲目。如果你已订阅却仍只播 30 秒，请前往系统「设置」→「隐私与安全性」→「媒体与 Apple Music」，确认 Moodshot 的访问权限处于开启状态。"
      },
      {
        question: "提示找不到歌单",
        answer: "画面与氛围识别完全在本地运行、无需联网；但检索并拉取歌单需要网络连接。请检查设备网络，网络恢复后 App 会自动重新发起检索。"
      },
      {
        question: "识别出来的氛围不对",
        answer:
          "轻点唱片正面 OTHER PRESSINGS 下方的备选名称，即可切换为排在第二、第三位的候补氛围。氛围名下方的一行小字记录了 App 当时识别到的场景、时段与光线冷暖，把推荐依据明明白白展示在眼前。若拍摄画面缺乏清晰细节（例如一面白墙或极暗的房间），能提取的光影线索较少，系统会退回到主要依据画面整体色调与当前时段来为你挑选唱片。"
      },
      {
        question: "收藏存在哪里？",
        answer:
          "全部保存在当前设备本地的 App 私有存储目录中，从未上传至我们的服务器，因此我们也无法远程协助找回。iCloud 同步默认开启，开启期间它们也会被复制进你自己的 iCloud 私有数据库——同样只有你能访问，我们依旧看不到也找不回，你可以随时在设置里关闭同步并移除这份副本。若卸载 App，本地收藏将被清除；如果你开启了 iCloud 备份或电脑备份，这些数据会随系统备份一同留存。"
      },
      {
        question: "会上传我的照片吗？",
        answer: "不会上传到我们的服务器。所有的场景与色调分析均由设备本地算力实时完成。iCloud 同步默认开启，开启期间收藏的照片会被复制进你自己的 iCloud 私有空间，我们同样看不到，你可以随时在设置里关闭同步并移除这份副本。"
      }
    ]
  },
  "zh-Hant": {
    title: "支援",
    introTitle: "需要什麼幫助？",
    introBody: "Moodshot 是一個人做的小 App。哪裡壞了、看不明白，或者少了什麼，寫信是最快能被處理的方式。",
    contactHeading: "聯絡我們",
    contactDescription: "每封信都會看，通常幾天內回覆。中文或英文皆可。",
    email: "axqora@gmail.com",
    reportHeading: "回報問題時請附上",
    reportItems: [
      "裝置型號（例如 iPhone 15 或 iPad Air）",
      "iOS 版本",
      "Moodshot 版本，在設定 → 關於裡",
      "問題發生前的具體操作步驟，以及實際發生了什麼",
      "截圖（附上截圖有助於我們更快排查）"
    ],
    faqHeading: "常見問題",
    faqs: [
      {
        question: "每首歌只播 30 秒",
        answer:
          "這是試聽模式。當目前裝置所登入的 Apple ID 未訂閱有效的 Apple Music 時，系統會播放 Apple 官方提供的 30 秒試聽。訂閱後即可收聽完整曲目。如果你已經訂閱卻仍只播 30 秒，請前往系統「設定」→「隱私權與安全性」→「媒體與 Apple Music」，確認 Moodshot 的存取權限處於開啟狀態。"
      },
      {
        question: "提示找不到播放列表",
        answer: "畫面與氛圍辨識完全在裝置端運行、無需連網；但檢索並拉取播放列表需要網路連線。請檢查裝置網路，網路恢復後 App 會自動重新發起檢索。"
      },
      {
        question: "辨識出來的氛圍不對",
        answer:
          "輕點唱片正面 OTHER PRESSINGS 下方的備選名稱，即可切換為排在第二、第三位的候補氛圍。氛圍名下方的一行小字記錄了 App 當時辨識到的場景、時段與光線冷暖，把推薦依據明明白白展示在眼前。若拍攝畫面缺乏清楚細節（例如一面白牆或極暗的房間），能擷取的光影線索較少，系統會退回主要依據畫面整體色調與目前時段來為你挑選唱片。"
      },
      {
        question: "收藏存在哪裡？",
        answer:
          "全部保存在目前裝置本機的 App 私有儲存目錄中，從未上傳至我們的伺服器，因此我們也無法遠端協助找回。iCloud 同步預設開啟，開啟期間它們也會被複製進你自己的 iCloud 私有資料庫——同樣只有你能存取，我們依舊看不到也找不回，你可以隨時在設定裡關閉同步並移除這份副本。若解除安裝 App，本機收藏將被清除；如果你開啟了 iCloud 備份或電腦備份，這些資料會隨系統備份一同留存。"
      },
      {
        question: "會上傳我的照片嗎？",
        answer: "不會上傳到我們的伺服器。所有的場景與色調分析均由裝置本機算力即時完成。iCloud 同步預設開啟，開啟期間收藏的照片會被複製進你自己的 iCloud 私有空間，我們同樣看不到，你可以隨時在設定裡關閉同步並移除這份副本。"
      }
    ]
  }
};
